import api from './api';

/**
 * Simulation service.
 *
 * Phase 2 onward: talks to the real API. The fixture-backed implementation this
 * replaced had identical signatures and return shapes, which is why no page or
 * component needed changing when the backend landed.
 *
 * Mongo returns `_id`; the UI keys on `id`. Normalising here rather than in each
 * component keeps that detail in one place — and the @dnd-kit sortable lists elsewhere
 * in this codebase specifically require a stable `id`, not `_id`.
 */

const withId = (sim) => (sim ? { ...sim, id: sim.id || sim._id } : sim);

const simulationService = {
  /** GET /simulations */
  list: async () => {
    const res = await api.get('/simulations');
    return { simulations: (res.simulations || []).map(withId) };
  },

  /** GET /simulations/:id */
  get: async (id) => {
    const res = await api.get(`/simulations/${id}`);
    return { simulation: withId(res.simulation) };
  },

  /** POST /simulations — accepts the intake questionnaire or a full document. */
  create: async (intake) => {
    const res = await api.post('/simulations', intake);
    return { simulation: withId(res.simulation) };
  },

  /** PUT /simulations/:id */
  update: async (id, patch) => {
    const res = await api.put(`/simulations/${id}`, patch);
    return { simulation: withId(res.simulation) };
  },

  /**
   * POST /simulations/:id/generate — starts the AI pipeline and returns 202 immediately.
   * A full run takes ~2 minutes, which is past any router timeout, so progress arrives
   * over `sim:generation-progress` and is also persisted on the document.
   */
  generate: (id, intake) => api.post(`/simulations/${id}/generate`, intake ? { intake } : {}),

  // ---------- reference documents ----------
  listSources: (id) => api.get(`/simulations/${id}/sources`),
  getSource: (id, sourceId) => api.get(`/simulations/${id}/sources/${sourceId}`),
  addSource: (id, file, { retainOriginal = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('retainOriginal', String(retainOriginal));
    return api.upload(`/simulations/${id}/sources`, fd);
  },
  updateSource: (id, sourceId, patch) => api.put(`/simulations/${id}/sources/${sourceId}`, patch),
  deleteSource: (id, sourceId) => api.delete(`/simulations/${id}/sources/${sourceId}`),
  /** Short-lived signed URL; the original is only kept if the trainer opted in. */
  downloadSource: (id, sourceId) => api.get(`/simulations/${id}/sources/${sourceId}/download`),

  /** POST /simulations/:id/clone */
  clone: async (id) => {
    const res = await api.post(`/simulations/${id}/clone`);
    return { simulation: withId(res.simulation) };
  },

  /** DELETE /simulations/:id — archives rather than destroys. */
  remove: async (id) => api.delete(`/simulations/${id}`),

  /**
   * POST /simulations/import — deterministic .docx/.xlsx parse, no AI.
   * Returns the created simulation plus any contradictions found between the two
   * files, which the builder surfaces rather than resolving silently.
   */
  importFiles: async ({ docx, xlsx, title }) => {
    const fd = new FormData();
    if (docx) fd.append('docx', docx);
    if (xlsx) fd.append('xlsx', xlsx);
    if (title) fd.append('title', title);
    const res = await api.upload('/simulations/import', fd);
    return { simulation: withId(res.simulation), conflicts: res.conflicts || [] };
  },
};

export default simulationService;
